import {
  Anchor,
  Badge,
  Box,
  Card,
  Container,
  Divider,
  Grid,
  Group,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Title,
  useMantineTheme,
} from '@mantine/core';

import {
  IconBed,
  IconBuilding,
  IconBuildingCastle,
  IconBuildingChurch,
  IconCalendar,
  IconClock,
  IconCoffee,
  IconMapPin,
  IconPhone,
  IconToolsKitchen2,
} from '@tabler/icons-react';

import { motion } from 'framer-motion';

import { PRIMARY_FONT } from '../../constants/fonts';

const WeddingInfoSection = () => {
  const theme = useMantineTheme();
  const infoCards = [
    {
      icon: IconBuildingChurch,
      title: 'Ceremonia Ślubna',
      subtitle: 'Kościół',
      location: 'Parafia pw. św. Stanisława Biskupa w Rydzynie',
      address: 'Kościelna 1, 64-130 Rydzyna',
      time: '19 lipca 2025, godz. 15:00',
      badge: 'Ceremonia',
      useSecondaryColor: true,
    },
    {
      icon: IconBuildingCastle,
      title: 'Przyjęcie Weselne',
      subtitle: 'Zamek',
      location: 'Zamek Królewski w Rydzynie',
      address: 'plac Zamkowy 1, 64-130 Rydzyna',
      time: '16:00 po mszy',
      badge: 'Wesele',
      useSecondaryColor: true,
    },
    {
      icon: IconBed,
      title: 'Nocleg',
      subtitle: 'Zakwaterowanie',
      location: 'Zamek Królewski w Rydzynie',
      address: 'plac Zamkowy 1, 64-130 Rydzyna',
      time: 'Nocleg w dniu wesela zapewniony',
      details: '*Możliwość wykupienia pokoju dzień wcześniej',
      badge: 'Hotel',
      useSecondaryColor: true,
    },
    {
      icon: IconToolsKitchen2,
      title: 'Dieta Specjalna',
      subtitle: 'Wymagania żywieniowe',
      location: 'Osoby z dietą wegetariańską lub wegańską',
      address: 'prosimy o kontakt z Parą Młodą',
      badge: 'Dieta',
      useSecondaryColor: true,
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 40,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
    },
  };

  return (
    <Box
      id="info"
      py={100}
      style={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Decorative background elements */}
      <Box
        style={{
          position: 'absolute',
          top: '10%',
          right: '5%',
          width: '300px',
          height: '300px',
          background: `radial-gradient(circle, rgba(${parseInt(theme.other.titleColor.slice(1, 3), 16)}, ${parseInt(theme.other.titleColor.slice(3, 5), 16)}, ${parseInt(theme.other.titleColor.slice(5, 7), 16)}, 0.08) 0%, transparent 70%)`,
          borderRadius: '50%',
          filter: 'blur(40px)',
          zIndex: 0,
        }}
      />
      <Box
        style={{
          position: 'absolute',
          bottom: '15%',
          left: '8%',
          width: '250px',
          height: '250px',
          background: `radial-gradient(circle, rgba(${parseInt(theme.other.titleColor.slice(1, 3), 16)}, ${parseInt(theme.other.titleColor.slice(3, 5), 16)}, ${parseInt(theme.other.titleColor.slice(5, 7), 16)}, 0.08) 0%, transparent 70%)`,
          borderRadius: '50%',
          filter: 'blur(40px)',
          zIndex: 0,
        }}
      />
      <Box
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '400px',
          height: '400px',
          background: 'radial-gradient(circle, rgba(59, 130, 246, 0.04) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(60px)',
          zIndex: 0,
        }}
      />
      <Box
        style={{
          position: 'absolute',
          top: '30%',
          left: '10%',
          width: '200px',
          height: '200px',
          background: `radial-gradient(circle, rgba(${parseInt(theme.other.secondaryColor.slice(1, 3), 16)}, ${parseInt(theme.other.secondaryColor.slice(3, 5), 16)}, ${parseInt(theme.other.secondaryColor.slice(5, 7), 16)}, 0.08) 0%, transparent 70%)`,
          borderRadius: '50%',
          filter: 'blur(40px)',
          zIndex: 0,
        }}
      />

      <Container size="lg" style={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
          viewport={{ once: true }}
        >
          <Stack align="center" gap="lg" mb={60}>
            <Badge
              size="lg"
              variant="light"
              color={theme.other.titleColor}
              style={{
                fontSize: '0.9rem',
                padding: '8px 16px',
                fontWeight: 500,
              }}
            >
              Szczegóły wydarzenia
            </Badge>

            <Title
              order={2}
              ta="center"
              style={{
                fontFamily: 'Playfair Display, serif',
                fontSize: 'clamp(2.5rem, 6vw, 4rem)',
                fontWeight: 700,
                color: theme.other.titleColor,
                lineHeight: 1.2,
                marginBottom: '1rem',
              }}
            >
              Informacje o ślubie
            </Title>

            <Text
              size="lg"
              ta="center"
              c="dimmed"
              maw={600}
              style={{
                fontSize: '1.1rem',
                lineHeight: 1.6,
                fontFamily: PRIMARY_FONT,
              }}
            >
              Wszystkie najważniejsze informacje dotyczące naszego wielkiego dnia
            </Text>
          </Stack>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
        >
          <Grid gutter="xl">
            {infoCards.map((card, index) => (
              <Grid.Col key={index} span={{ base: 12, sm: 6 }}>
                <motion.div variants={cardVariants}>
                  <Paper
                    radius="24"
                    p={0}
                    style={{
                      height: '100%',
                      background: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      boxShadow: `0 20px 40px ${theme.other.titleColor}15, 0 8px 32px ${theme.other.titleColor}10, 0 4px 16px rgba(0, 0, 0, 0.05)`,
                      overflow: 'hidden',
                      position: 'relative',
                      transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    }}
                    styles={{
                      root: {
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 25px 50px ${theme.other.titleColor}20, 0 12px 40px ${theme.other.titleColor}15, 0 6px 20px rgba(0, 0, 0, 0.08)`,
                        },
                      },
                    }}
                  >
                    {/* Card header with icon and badge */}
                    <Box
                      p="xl"
                      style={{
                        background: 'rgba(255, 255, 255, 0.5)',
                        backdropFilter: 'blur(8px)',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                      }}
                    >
                      <Group justify="space-between" align="flex-start" mb="md">
                        <Box
                          style={{
                            width: 56,
                            height: 56,
                            borderRadius: '16px',
                            background: card.useSecondaryColor
                              ? `linear-gradient(135deg, ${theme.other.secondaryColor} 0%, ${theme.other.secondaryColor}dd 100%)`
                              : `linear-gradient(135deg, ${theme.other.titleColor} 0%, ${theme.other.titleColor}dd 100%)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: card.useSecondaryColor
                              ? `0 4px 20px ${theme.other.secondaryColor}30`
                              : `0 4px 20px ${theme.other.titleColor}30`,
                          }}
                        >
                          <card.icon size={28} color={card.useSecondaryColor ? 'black' : 'white'} />
                        </Box>

                        <Badge
                          variant="light"
                          size="md"
                          style={{
                            minWidth: '80px',
                            fontSize: '1rem',
                            fontWeight: 600,
                            padding: '12px 16px',
                            fontFamily: PRIMARY_FONT,
                            backgroundColor: card.useSecondaryColor ? theme.other.secondaryColor : theme.other.titleColor,
                            color: card.useSecondaryColor ? 'black' : 'white',
                          }}
                        >
                          {card.badge}
                        </Badge>
                      </Group>

                      <Title
                          order={2}
                          size="xl"
                          style={{
                            fontFamily: PRIMARY_FONT,
                            color: '#1a1a1a',
                            fontWeight: 700,
                            lineHeight: 1.3,
                            fontSize: '1.5rem',
                          }}
                        >
                          {card.title}
                        </Title>
                    </Box>

                    {/* Card content */}
                    <Stack gap="md" p="xl">
                      <Group gap="xs" align="flex-start">
                        <Box
                          style={{
                            width: 24,
                            height: 24,
                            borderRadius: '6px',
                            background: `${theme.other.titleColor}15`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginTop: 2,
                          }}
                        >
                          <IconMapPin size={14} color={theme.other.titleColor} />
                        </Box>
                        <Stack gap={4} style={{ flex: 1 }}>
                          <Text
                            fw={600}
                            size="sm"
                            c="dark"
                            style={{
                              fontFamily: PRIMARY_FONT,
                            }}
                          >
                            {card.location}
                          </Text>
                          <Text
                            size="sm"
                            style={{
                              lineHeight: 1.4,
                              fontFamily: PRIMARY_FONT,
                            }}
                          >
                            {card.address}
                          </Text>
                        </Stack>
                      </Group>

                      {card.time && (
                        <>
                          <Divider color="gray" opacity={0.1} />
                          <Group gap="xs" align="flex-start">
                            <Box
                              style={{
                                width: 24,
                                height: 24,
                                borderRadius: '6px',
                                background: `${theme.other.titleColor}15`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginTop: 2,
                              }}
                            >
                              <IconClock size={14} color={theme.other.titleColor} />
                            </Box>
                            <Text
                              fw={500}
                              size="sm"
                              c="dark"
                              style={{
                                lineHeight: 1.4,
                                fontFamily: PRIMARY_FONT,
                              }}
                            >
                              {card.time}
                            </Text>
                          </Group>
                        </>
                      )}

                      {card.details && (
                        <>
                          <Divider color="gray" opacity={0.1} />
                          <Text
                            size="sm"
                            c="dimmed"
                            fs="italic"
                            style={{
                              lineHeight: 1.4,
                              fontFamily: PRIMARY_FONT,
                            }}
                          >
                            {card.details}
                          </Text>
                        </>
                      )}
                    </Stack>
                  </Paper>
                </motion.div>
              </Grid.Col>
            ))}
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
};

export { WeddingInfoSection };
export default WeddingInfoSection;
